import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';

type AbortHandle = { abort: (reason?: string) => void };

/**
 * The hook manages the full abort lifecycle:
 * - Aborts the previous in-flight request before starting a new one
 * - Aborts on unmount
 * - Aborts when query drops below minLength (e.g. on clear)
 *
 * `searchFn` is stored in a ref so callers don't need to memoise it —
 * the debounce instance stays stable across renders regardless.
 *
 * @param searchFn  Should dispatch the thunk and return the abortable handle
 *                  (i.e. `return dispatch(myThunk(args))`)
 * @returns `{ searchNow }` — call this for immediate (non-debounced) triggers like Enter or icon click
 */
const useDebouncedSearch = (
    searchFn: () => AbortHandle | void | null,
    query: string,
    delayMs: number,
    minLength = 2,
) => {
    // ── refs ────────────────────────────────────────────────────
    const abortRef = useRef<AbortHandle | null>(null);
    const searchFnRef = useRef(searchFn);
    searchFnRef.current = searchFn; // always points at the latest closure

    // ── stable helpers (never recreated) ────────────────────────
    const executeSearch = useCallback(() => {
        abortRef.current?.abort();
        const handle = searchFnRef.current();
        abortRef.current = handle ?? null;
    }, []);

    const debouncedSearch = useMemo(
        () => debounce(executeSearch, delayMs),
        [executeSearch, delayMs],
    );

    // ── auto-search on query change ─────────────────────────────
    useEffect(() => {
        if (query.length >= minLength) {
            debouncedSearch();
        } else {
            debouncedSearch.cancel();
            abortRef.current?.abort();
            abortRef.current = null;
        }
        return () => debouncedSearch.cancel();
    }, [query, debouncedSearch, minLength]);

    // ── cleanup on unmount ──────────────────────────────────────
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
            abortRef.current?.abort();
        };
    }, [debouncedSearch]);

    // ── immediate trigger (Enter / icon click) ──────────────────
    const searchNow = useCallback(() => {
        if (query.length < minLength) return;
        debouncedSearch.cancel();
        executeSearch();
    }, [debouncedSearch, executeSearch, query, minLength]);

    return { searchNow };
};

export default useDebouncedSearch;
